import { Grid, Typography, ThemeProvider, Chip } from '@mui/material';
import { NextPage } from 'next';
import { useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Policy, Forbidden } from 'ui';
import theme from '../styles/theme';

import useUser from '../auth/useUser';
import { deactivateUser, getUsers } from '../http/api/users';
import { EditUserModal } from '../components/editUserModal';
import { updateUser } from '../http/api/users';
import { IUser, PermissionsEnum } from 'permisos-common';
import { UsersDataTable } from '../components/usersDataTable';
import { FormSubmitSnackbar } from '../components/formSubmitSnackbar';
import ActionsRenderer from '../components/CellRenderers/ActionsRenderer';
import {
  ColDef,
  GridApi,
  ICellRendererParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { Block, EditOutlined } from '@mui/icons-material';
import { DateTime } from 'luxon';
import { ConfirmDeactiveUserDialog } from '../components/confirmDeactiveUserDialog';

const UsersPage: NextPage = () => {
  const currentUser = useUser();
  const gridRef = useRef<GridApi | null>(null);

  const [isEditUserDialogOpen, setIsEditUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [openEditUserFormSubmitAlert, setOpenEditUserFormSubmitAlert] =
    useState(false);

  const [isConfirmDeactiveUserDialogOpen, setIsConfirmDeactiveUserDialogOpen] =
    useState(false);

  const onDeactiveUser = (user: IUser) => {
    setIsConfirmDeactiveUserDialogOpen(true);
    setSelectedUser(user);
  };

  const onDeactiveUserClose = () => {
    setIsConfirmDeactiveUserDialogOpen(false);
  };

  const onDeactiveUserConfirm = () => {
    const username = selectedUser?.username;
    if (username) {
      deactivateUserMutation.mutate(username);
    }
    setIsConfirmDeactiveUserDialogOpen(false);
  };

  const [deactivationMessage, setDeactivationMessage] = useState('');
  const [openDeactivationAlert, setOpenDeactivationAlert] = useState(false);

  const deactivateUserMutation = useMutation({
    mutationFn: deactivateUser,
    onSuccess: (data) => {
      setDeactivationMessage(data.message);
      setOpenDeactivationAlert(true);
      queryClient.invalidateQueries({ queryKey: ['getUsers'] });
      gridRef.current?.refreshServerSide?.();
    },
  });

  const hasManagePermission = currentUser?.roles.some(
    (permission) => permission === PermissionsEnum.ManagePermissions
  );

  const queryClient = useQueryClient();

  const onEditUser = (user: IUser) => {
    setIsEditUserDialogOpen(true);
    setSelectedUser(user);
  };

  const editUserMutation = useMutation({
    mutationFn: updateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getUsers'] });
      queryClient.invalidateQueries({ queryKey: ['getUserRoles'] });
      setOpenEditUserFormSubmitAlert(true);
      gridRef.current?.refreshServerSide();
    },
  });

  const formatLastLoginBody = (timestamp?: number | null) => {
    if (!timestamp) {
      return '-';
    }

    const dateTime = DateTime.fromMillis(timestamp).setZone('America/Bogota');
    return dateTime.toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS);
  };

  const columnDefs: ColDef<any>[] = useMemo(
    () => [
      {
        field: 'username',
        headerName: 'Nombre de usuario',
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          maxNumConditions: 1,
        },
        sortable: true,
      },
      {
        field: 'osfUsername',
        headerName: 'Nombre de usuario Osf',
        cellRenderer: (params: ICellRendererParams) => {
          if (!params?.value || params?.value === '') {
            return '--';
          }
          return params?.value;
        },
        sortable: false,
      },
      {
        field: 'onbaseUsername',
        headerName: 'Nombre de usuario OnBase',
        cellRenderer: (params: ICellRendererParams) => {
          if (!params?.value || params?.value === '') {
            return '--';
          }
          return params?.value;
        },
        sortable: false,
      },
      {
        field: 'isActive',
        headerName: 'Estado',
        cellRenderer: (params: ICellRendererParams) => {
          const isActive = params.value;
          return (
            <Chip
              label={isActive ? 'ACTIVO' : 'INACTIVO'}
              variant="outlined"
              size="small"
              sx={{
                fontWeight: 600,
                color: isActive ? '#1565c0' : '#c62828',
                backgroundColor: isActive ? '#e3f2fd' : '#ffebee',
                borderColor: 'transparent',
                textTransform: 'uppercase',
              }}
            />
          );
        },
        sortable: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [true, false],
          valueFormatter: (params: any) => {
            return params.value ? 'ACTIVO' : 'INACTIVO';
          },
        },
        minWidth: 150,
      },
      {
        field: 'lastLogin',
        headerName: 'Último ingreso',
        minWidth: 190,
        filter: 'agDateColumnFilter',
        sortable: true,
        valueGetter: (params: ValueGetterParams) => {
          const lastLogin = params.data?.lastLogin;
          if (!lastLogin) {
            return null;
          }

          const date = new Date(lastLogin);
          return isNaN(date.getTime()) ? null : date.getTime();
        },
        valueFormatter: (params) => formatLastLoginBody(params.value),
        filterParams: {
          filterOptions: ['equals'],
          maxNumConditions: 1,
        },
      },
      {
        headerName: 'Acciones',
        cellRenderer: ActionsRenderer,
        resizable: false,
        sortable: false,
        maxWidth: 150,
        minWidth: 150,
        width: 150,
        filter: false,
        cellRendererParams: {
          actions: [
            {
              kind: 'function',
              Icon: (user: IUser) => (
                <EditOutlined
                  style={{
                    color:
                      hasManagePermission && user.isActive
                        ? '#1976D2'
                        : 'primary',
                  }}
                />
              ),
              tooltip: 'Editar',
              disabled: (user: IUser) => !hasManagePermission || !user.isActive,
              onAction: onEditUser,
            },
            {
              kind: 'function',
              Icon: (user: IUser) => (
                <Block
                  style={{
                    color: user.isActive ? 'red' : 'primary',
                  }}
                />
              ),
              tooltip: 'Desactivar usuario',
              disabled: (user: IUser) => !hasManagePermission || !user.isActive,
              onAction: onDeactiveUser,
            },
          ],
        },
      },
    ],
    [hasManagePermission]
  );

  const getUsersMutation = useMutation({
    mutationFn: getUsers,
  });

  return (
    <Policy
      user={currentUser}
      roles={['manage_permissions']}
      fallback={
        <Forbidden
          text={'No tienes permiso para acceder a la aplicación'}
          userLogout={currentUser?.actions?.logout}
          email={currentUser?.email}
        />
      }
    >
      <ThemeProvider theme={theme}>
        <Grid
          container
          sx={{
            background: theme.palette.background.paper,
            boxShadow: theme.shadows[4],
            borderRadius: 1,
            marginTop: 2,
            padding: 2,
          }}
          rowSpacing={2}
          columnSpacing={2}
        >
          <Grid size={12}>
            <Typography
              sx={{
                fontWeight: 500,
                color: theme.palette.text.primary,
              }}
              variant="h5"
              component="h2"
            >
              Usuarios
            </Typography>
          </Grid>
          <Grid style={{ width: '100%' }} size={12}>
            <UsersDataTable
              columnDefs={columnDefs}
              getUsersMutation={getUsersMutation}
              gridRef={gridRef}
            />
          </Grid>
        </Grid>
        <EditUserModal
          isOpen={isEditUserDialogOpen}
          toggleOpen={setIsEditUserDialogOpen}
          selectedUser={selectedUser}
          editUserMutation={editUserMutation}
        />
        <FormSubmitSnackbar
          isOpen={openEditUserFormSubmitAlert}
          onClose={() => setOpenEditUserFormSubmitAlert(false)}
          severity="success"
          text={'¡Usuario editado exitosamente!'}
        />
        <FormSubmitSnackbar
          isOpen={openDeactivationAlert}
          onClose={() => setOpenDeactivationAlert(false)}
          severity="success"
          text={deactivationMessage}
        />
        <ConfirmDeactiveUserDialog
          onDeactiveUserConfirm={onDeactiveUserConfirm}
          onDeactiveUserClose={onDeactiveUserClose}
          isOpen={isConfirmDeactiveUserDialogOpen}
          selectedUser={selectedUser}
        />
      </ThemeProvider>
    </Policy>
  );
};

export default UsersPage;
